<template>
  <div>
    <div class="flex items-center gap-4 px-4 py-3 border-b border-gray-100">
      <button type="button" :class="TEXT_BUTTON" @click="addCase">Add case</button>
      <button type="button" :class="TEXT_BUTTON" @click="showImport = !showImport">Import JSON</button>
      <button type="button" :class="TEXT_BUTTON" @click="exportJson">Export JSON</button>
      <div v-if="dirty" class="ml-auto flex items-center gap-3">
        <span class="text-gray-600">Unsaved changes</span>
        <button type="button" :class="TEXT_BUTTON" @click="reset">Discard</button>
        <button type="button" :class="PRIMARY_BUTTON" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
      </div>
    </div>
    <p v-if="saveError" class="px-4 pt-3 text-red-600">{{ saveError }}</p>

    <div v-if="showImport" class="px-4 py-4 border-b border-gray-100">
      <span class="block text-gray-500 mb-2">A JSON list of cases: {key, label, tenant, prompt, user_context, answer, must[], must_not[], truth_author, source {type, ref, note}, hats}. Replaces the whole list; save afterwards.</span>
      <textarea v-model="importText" :class="[INPUT, 'font-mono h-40']" />
      <div class="flex items-center gap-3 mt-2">
        <button type="button" :class="TEXT_BUTTON" @click="importJson">Replace list</button>
        <span v-if="importError" class="text-red-600">{{ importError }}</span>
      </div>
    </div>

    <p v-if="!cases.length" class="px-4 py-6 text-gray-500">No cases yet. Add one from a turn on the Console tab.</p>
    <ul v-else class="divide-y divide-gray-100">
      <li v-for="(c, i) in cases" :key="i" class="flex items-start gap-4 px-4 py-3">
        <input type="checkbox" class="mt-1" :checked="selected.includes(c.key)" :disabled="!hasTruth(c)" title="Include in the next run" @change="$emit('select', c.key)">
        <div class="min-w-0 flex-1">
          <div class="flex items-baseline gap-2">
            <span class="font-medium text-gray-900">{{ c.label }}</span>
            <span class="text-gray-500">{{ c.tenant || bench.tenantName }}</span>
            <span class="text-gray-500">· {{ c.source.type }}{{ c.source.ref ? ` ${c.source.ref}` : '' }}</span>
          </div>
          <p class="sensitive mt-0.5 truncate text-gray-600" :title="c.prompt" data-demo-blur>{{ c.prompt || '—' }}</p>
          <p v-if="hasTruth(c)" class="mt-0.5 text-gray-500">
            {{ truthLine(c) }}<template v-if="c.truth_author"> · answer from {{ c.truth_author }}</template>
          </p>
          <p v-else class="mt-0.5 text-amber-700">Needs the right answer before it can run.</p>
        </div>
        <div class="flex shrink-0 items-center gap-3 pt-0.5">
          <a v-if="c.source_session" :href="`/agent/${c.source_session}`" target="_blank" rel="noopener" :class="TEXT_BUTTON">Source</a>
          <button type="button" :class="TEXT_BUTTON" @click="edit(i)">Edit</button>
          <button type="button" :class="TEXT_BUTTON" @click="remove(i)">Remove</button>
        </div>
      </li>
    </ul>

    <div v-if="editing !== null" class="fixed inset-0 z-40 bg-black/30 flex items-center justify-center" @click.self="editing = null">
      <div class="bg-white rounded-lg border border-gray-200 w-[60rem] max-w-[95vw] max-h-[90vh] flex flex-col">
        <div class="px-5 py-3 border-b border-gray-100 flex items-center">
          <h3 class="font-semibold text-gray-900">{{ draft.label || 'New case' }}</h3>
          <button class="ml-auto text-gray-400 hover:text-gray-600" @click="editing = null">×</button>
        </div>
        <div class="p-5 overflow-y-auto grid grid-cols-12 gap-4">
          <label class="col-span-6"><span class="block text-gray-500 mb-2">Label</span><input v-model="draft.label" :class="INPUT"></label>
          <label class="col-span-2"><span class="block text-gray-500 mb-2">Tenant</span><input v-model="draft.tenant" :class="INPUT" placeholder="vytalize"></label>
          <label class="col-span-4"><span class="block text-gray-500 mb-2">Required hat path (optional)</span><input v-model="draft.hats" :class="INPUT" placeholder="population,reporter"></label>
          <label class="col-span-12">
            <span class="block text-gray-500 mb-2">Opening prompt</span>
            <textarea v-model="draft.prompt" :class="[INPUT, 'sensitive h-32']" data-demo-blur />
          </label>
          <label class="col-span-12">
            <span class="block text-gray-500 mb-2">What the user knows (the judge, playing the user, may tell the agent this; never the answer)</span>
            <textarea v-model="draft.user_context" :class="[INPUT, 'sensitive h-20']" data-demo-blur />
          </label>
          <div class="col-span-12 border-t border-gray-100 pt-4"><span class="font-medium text-gray-900">Ground truth</span><span class="text-gray-500"> · only the scoring judge sees this</span></div>
          <label class="col-span-12">
            <span class="block text-gray-500 mb-2">The right answer: the record, number or conclusion</span>
            <textarea v-model="draft.answer" :class="[INPUT, 'sensitive h-24']" data-demo-blur />
          </label>
          <label class="col-span-6">
            <span class="block text-gray-500 mb-2">Must do or say, one per line</span>
            <textarea v-model="draft.must" :class="[INPUT, 'sensitive h-28']" data-demo-blur />
          </label>
          <label class="col-span-6">
            <span class="block text-gray-500 mb-2">Must not do or say, one per line</span>
            <textarea v-model="draft.must_not" :class="[INPUT, 'sensitive h-28']" data-demo-blur />
          </label>
          <label class="col-span-4"><span class="block text-gray-500 mb-2">Who established the answer</span><input v-model="draft.truth_author" :class="INPUT" placeholder="Cindy, resolution on ticket 7942"></label>
          <label class="col-span-2">
            <span class="block text-gray-500 mb-2">Source</span>
            <select v-model="draft.source_type" :class="INPUT"><option v-for="t in SOURCE_TYPES" :key="t" :value="t">{{ t }}</option></select>
          </label>
          <label class="col-span-2"><span class="block text-gray-500 mb-2">Reference</span><input v-model="draft.source_ref" :class="INPUT" placeholder="7942"></label>
          <label class="col-span-4"><span class="block text-gray-500 mb-2">Note on the source</span><input v-model="draft.source_note" :class="INPUT"></label>
          <p v-if="draft.rubric" class="col-span-12 text-gray-500">Old free-text rubric, used only while the case has no answer or statements: <span class="sensitive" data-demo-blur>{{ draft.rubric }}</span></p>
          <p class="col-span-12 text-gray-500">The judge plays the user from the opening prompt and what the user knows, follows up until the conversation is done, and never approves a write. A separate judge then grades each criterion against the right answer, quoting the transcript.</p>
        </div>
        <div class="px-5 py-3 border-t border-gray-100 flex items-center gap-3">
          <span v-if="draftError" class="text-red-600">{{ draftError }}</span>
          <button type="button" :class="[SECONDARY_BUTTON, 'ml-auto']" @click="editing = null">Cancel</button>
          <button type="button" :class="PRIMARY_BUTTON" @click="applyDraft">Done</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { exportBlockedInDemoMode } from '@/utils/demoSpoof';
import { INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TEXT_BUTTON, errorOf } from '../hermes';
import { SOURCE_TYPES, caseFrom, casesOf, hasTruth, saveCases } from './bench';

function toDraft(c) {
  return {
    key: c.key, label: c.label, hats: c.hats, tenant: c.tenant, prompt: c.prompt, user_context: c.user_context,
    answer: c.answer, must: c.must.join('\n'), must_not: c.must_not.join('\n'), truth_author: c.truth_author,
    source_type: c.source.type, source_ref: c.source.ref, source_note: c.source.note, rubric: c.rubric,
    source_session: c.source_session, source_run: c.source_run,
  };
}

function fromDraft(d) {
  return caseFrom({
    key: d.key, label: d.label.trim(), hats: d.hats.trim(), tenant: d.tenant.trim(), prompt: d.prompt.trim(),
    user_context: d.user_context.trim(), answer: d.answer.trim(), must: d.must, must_not: d.must_not,
    truth_author: d.truth_author.trim(), source: { type: d.source_type, ref: d.source_ref.trim(), note: d.source_note.trim() },
    rubric: d.rubric, source_session: d.source_session, source_run: d.source_run,
  });
}

export default {
  name: 'TestCases',
  props: { bench: { type: Object, required: true }, selected: { type: Array, default: () => [] } },
  emits: ['refresh', 'select'],
  data() {
    const cases = casesOf(this.bench.template);
    return {
      cases, original: JSON.stringify(cases), saving: false, saveError: '',
      showImport: false, importText: '', importError: '', editing: null, draft: toDraft(caseFrom({})), draftError: '',
      INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TEXT_BUTTON, SOURCE_TYPES,
    };
  },
  computed: {
    dirty() {
      return JSON.stringify(this.cases) !== this.original;
    },
  },
  methods: {
    hasTruth,
    truthLine(c) {
      const bits = [];
      if (c.answer) bits.push('answer');
      if (c.must.length) bits.push(`${c.must.length} must`);
      if (c.must_not.length) bits.push(`${c.must_not.length} must not`);
      return bits.length ? bits.join(' · ') : 'old rubric only';
    },
    plain(c) {
      const copy = { ...c };
      delete copy.kind;
      return copy;
    },
    addCase() {
      this.cases.push(caseFrom({ label: `Case ${this.cases.length + 1}`, key: `case_${Date.now()}` }));
      this.edit(this.cases.length - 1);
    },
    edit(i) {
      this.draft = toDraft(this.cases[i]);
      this.draftError = '';
      this.editing = i;
    },
    applyDraft() {
      const c = fromDraft(this.draft);
      if (!c.label) { this.draftError = 'A case needs a label.'; return; }
      if (!c.prompt) { this.draftError = 'A case needs a prompt.'; return; }
      if (!c.key) c.key = c.label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 80);
      this.cases.splice(this.editing, 1, c);
      this.editing = null;
    },
    remove(i) {
      this.cases.splice(i, 1);
    },
    reset() {
      this.cases = JSON.parse(this.original);
    },
    importJson() {
      this.importError = '';
      let parsed;
      try {
        parsed = JSON.parse(this.importText);
      } catch (e) {
        this.importError = `Not valid JSON: ${e.message}`;
        return;
      }
      if (!Array.isArray(parsed) || !parsed.length) { this.importError = 'Expected a non-empty JSON list.'; return; }
      this.cases = parsed.map(caseFrom);
      this.showImport = false;
      this.importText = '';
    },
    exportJson() {
      if (exportBlockedInDemoMode()) return;
      const text = JSON.stringify(this.cases.map((c) => this.plain(c)), null, 2);
      const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'agent_bench_cases.json';
      a.click();
      URL.revokeObjectURL(url);
    },
    async save() {
      this.saving = true;
      this.saveError = '';
      try {
        await saveCases(this.$axios, this.bench, this.cases);
        this.original = JSON.stringify(this.cases);
        this.$emit('refresh');
      } catch (e) {
        this.saveError = errorOf(e, 'The cases were not saved.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
